import { getResizedImage } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { AchievementIcon } from 'components/achievements';
import {
  DisplayFormattedDate,
  LoadingIndicator,
  ProductLinkWithIcon
} from 'components/common';
import { Icon } from 'components/common/icons';
import { HomeDetailsItem } from '.';

import type { GetUserUpcomingDeadlinesRes } from 'shared-types/responses';

const HomeDetailsUpcomingTargetSchedules = () => {
  const { messages, theme, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const { data, loading } = useApiData<GetUserUpcomingDeadlinesRes>({
    url: `/admin/users/${user?.id}/upcoming-deadlines`
  });

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div className="deadlines">
      {data?.deadlines.map((deadline) => (
        <HomeDetailsItem
          href={
            deadline.type === 'course'
              ? `/courses/${deadline.course.slug}`
              : deadline.type === 'path'
              ? `/paths/${deadline.path.id}`
              : ''
          }
          key={deadline.id}
          category="warning-light"
          timeString={deadline.deadline as string}
        >
          {deadline.type === 'course' ? (
            <>
              <div
                className="picture"
                style={{
                  backgroundImage: `url(${getResizedImage({
                    url: deadline.course.picture,
                    size: 480
                  })})`,
                  backgroundPosition: deadline.course.picturePosition
                }}
              />

              <div className="inner">
                <div className="title">{deadline.course.title}</div>
                <div className="info">
                  <div>{messages.commonCourse}</div>

                  <div>
                    <span>{`${messages.courseTargetSchedule}: `}</span>
                    <span>
                      <DisplayFormattedDate date={deadline.deadline} />
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : deadline.type === 'path' ? (
            <>
              <div className="inner">
                <div className="info" style={{ marginTop: 0 }}>
                  <div>{`${messages.commonPath}: ${deadline.path.title}`}</div>
                  <div>{`${messages.pathsStep} ${deadline.pathStep.number}`}</div>
                  <div>
                    <span>{`${messages.courseTargetSchedule}: `}</span>
                    <span>
                      <DisplayFormattedDate date={deadline.deadline} />
                    </span>
                  </div>
                </div>

                <div className="step">
                  {deadline.pathStep.items?.length > 1 ? (
                    <>
                      <div className="step-icon">
                        <Icon icon="share-alt" />
                      </div>
                      <div className="step-content">
                        <div className="title">
                          {getMessageWithPlaceholders({
                            message: messages.commonNumberOfAlternatives,
                            placeholders: {
                              numberOfAlternatives:
                                deadline.pathStep.items.length
                            }
                          })}
                        </div>

                        <div className="items">
                          {deadline.pathStep.items.map((item) => (
                            <div className="item" key={item.id}>
                              {item.type === 'achievement' ? (
                                <ProductLinkWithIcon
                                  achievement={item.achievement}
                                  disableLink={true}
                                />
                              ) : item.type === 'event' ? (
                                <ProductLinkWithIcon
                                  calendarEvent={item.event}
                                  disableLink={true}
                                />
                              ) : item.type === 'course' ? (
                                <ProductLinkWithIcon
                                  course={item.course}
                                  disableLink={true}
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : deadline.pathStep.items?.length ? (
                    deadline.pathStep.items[0].type === 'achievement' ? (
                      <>
                        <AchievementIcon
                          type={deadline.pathStep.items[0].achievement.type}
                        />

                        <div className="step-content">
                          <div className="title">
                            {deadline.pathStep.items[0].achievement.title}
                          </div>
                          <div className="info">
                            {
                              achievementTypes[
                                deadline.pathStep.items[0].achievement.type
                              ].title
                            }
                          </div>
                        </div>
                      </>
                    ) : deadline.pathStep.items[0].type === 'event' ? (
                      <>
                        <div className="step-icon">
                          <Icon icon={['far', 'calendar-days']} />
                        </div>

                        <div className="step-content">
                          <div className="title">
                            {deadline.pathStep.items[0].event.title}
                          </div>
                          <div className="info">
                            {messages.eventCalendarEvent}
                          </div>
                        </div>
                      </>
                    ) : deadline.pathStep.items[0].type === 'course' ? (
                      <>
                        <div
                          className="picture"
                          style={{
                            backgroundImage: `url(${getResizedImage({
                              url: deadline.pathStep.items[0].course.picture,
                              size: 480
                            })})`,
                            backgroundPosition:
                              deadline.pathStep.items[0].course.picturePosition
                          }}
                        />

                        <div className="step-content">
                          <div className="title">
                            {deadline.pathStep.items[0].course.title}
                          </div>
                          <div className="info">{messages.commonCourse}</div>
                        </div>
                      </>
                    ) : null
                  ) : (
                    <div className="empty">{'No items'}</div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </HomeDetailsItem>
      ))}

      <style jsx>{`
        .deadlines {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .picture {
          background-size: cover;
          border-radius: 8px;
          flex-shrink: 0;
          height: 40px;
          width: 40px;
        }
        .inner {
          flex-grow: 1;
        }
        .title {
          font-size: 1.6rem;
          font-weight: 700;
        }
        .info {
          color: ${theme.textColorLighter};
          display: flex;
          flex-wrap: wrap;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 5px 40px;
          margin-top: 4px;
        }
        .step {
          display: flex;
          gap: 15px;
          margin-top: 10px;
        }
        .step-icon {
          align-items: center;
          background-color: ${theme.lightGrey};
          border-radius: 8px;
          display: inline-flex;
          flex-shrink: 0;
          font-size: 1.6rem;
          justify-content: center;
          height: 40px;
          width: 40px;
        }
        .items {
          margin-top: 5px;
        }
        .item {
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};
export default HomeDetailsUpcomingTargetSchedules;
